import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import {formatValueForTable} from '../../utils/formatValueForTable';

const FoodComparisonViewResult = ({result, status}) => {
  // Normalize the result structure to handle different response formats
  const isPending = status === 'pending';
  const normalizeResult = (rawResult) => {
    if ((!rawResult || typeof rawResult !== 'object') && !isPending) {
      return { status: { success: false, error: "No valid result data" }, items: [] };
    }

    // Case 1: Standard format with status and items array
    if (rawResult.status && Array.isArray(rawResult.items)) {
      return rawResult;
    }

    // Case 2: Format with status and output array
    if (rawResult.status && Array.isArray(rawResult.output)) {
      return {
        status: rawResult.status,
        items: rawResult.output
      };
    }

    // Case 3: Single comparison item (wrap it in our expected structure)
    if (rawResult.key && ('diff' in rawResult) && rawResult.version_1 && rawResult.version_2) {
      return {
        status: { success: true, error: null },
        items: [rawResult]
      };
    }

    // Case 4: Array of comparison items but no status
    if (Array.isArray(rawResult)) {
      return {
        status: { success: true, error: null },
        items: rawResult
      };
    }

    // Default fallback for unknown formats
    return {
      status: {
        success: null,
        pending: isPending,
        error: isPending ? null : "Unrecognized result format"
      },
      items: []
    };
  };

  // Use normalized result for all operations
  const normalizedResult = normalizeResult(result);

  // Check if there's an error in the status
  if (normalizedResult.status && normalizedResult.status.success === false) {
    return (
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-center gap-2 text-error">
          <PhosphorIcons.Warning size={20} weight="bold" />
          <p className="font-semibold">
            {normalizedResult.status.error || "An error occurred during food comparison"}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state for pending status
  if (normalizedResult.status?.pending) {
    return (
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-center gap-2 text-info">
          <div className="animate-spin">
            <PhosphorIcons.CircleNotch size={20} weight="bold"/>
          </div>
          <p className="font-semibold">Processing comparison results...</p>
        </div>
      </div>
    );
  }

  // Fallback if no items to display
  if (!normalizedResult.items || normalizedResult.items.length === 0) {
    return (
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-center gap-2 text-info">
          <PhosphorIcons.Info size={20} weight="bold" />
          <p className="font-semibold">No food comparison items were found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {/* Status indicator for successful comparison */}
      {normalizedResult.status && normalizedResult.status.success && (
        <div className="mb-2 px-4 py-2 border rounded shadow-sm bg-success/10 text-success">
          <div className="flex items-center gap-2">
            <PhosphorIcons.CheckCircle size={20} weight="bold" />
            <p className="font-semibold">Comparison completed successfully</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-base-300 rounded-lg shadow-sm bg-base-100">
        <table className="table table-zebra w-full">
          {/* Head */}
          <thead className="bg-base-200">
            <tr>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Property</th>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Version 1</th>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Version 2</th>
              <th className="p-3 text-center text-sm font-semibold text-base-content tracking-wider">Different?</th>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Reason</th>
            </tr>
          </thead>
          <tbody>
          {normalizedResult.items
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((item) => {
              return (
                <tr key={item.key} className={`hover:bg-base-200/50 ${item.diff ? 'bg-warning/10' : ''}`}>
                  {/* Property Name */}
                  <td className="p-3 text-sm font-medium text-base-content whitespace-nowrap">{item.key}</td>
                  {/* Version 1 */}
                  <td className="p-3 text-sm text-base-content/80">{formatValueForTable(item.version_1)}</td>
                  {/* Version 2 */}
                  <td className="p-3 text-sm text-base-content/80">{formatValueForTable(item.version_2)}</td>
                  {/* Different? */}
                  <td className="p-3 text-sm text-center">
                    {item.diff ? (
                      <span className="inline-flex items-center justify-center p-1.5 bg-warning/20 text-warning-content rounded-full">
                        <PhosphorIcons.Warning size={16} weight="bold" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center p-1.5 bg-success/20 text-success-content rounded-full">
                        <PhosphorIcons.Check size={16} weight="bold" />
                      </span>
                    )}
                  </td>
                  {/* Reason */}
                  <td className="p-3 text-sm text-base-content/80">{formatValueForTable(item.reason)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary section */}
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-start gap-2">
          <PhosphorIcons.Info size={20} weight="bold" className="text-info mt-0.5" />
          <div>
            <p className="font-semibold">Comparison Summary</p>
            <p className="text-sm mt-1">
              {normalizedResult.items.filter(item => item.diff).length} differences found out of {normalizedResult.items.length} properties compared.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodComparisonViewResult;
